import { isAdministrator, type Session } from '@/lib/types';

/**
 * **현장 소속 사람에게는 현장이 먼저 보인다** (사용자 결정 2026-09-02).
 *
 * 주간업무·월간업무는 본사 탭이 1번이었다. 매일 여는 사람이 현장 담당자인데 들어올 때마다 한 칸
 * 옆으로 옮겨 눌러야 했다. <b>순서만 바꾼다</b> — 무엇이 보이는지는 그대로이고, 그것은 메뉴 권한이
 * 정한다. 이 파일은 「누가 무엇을 볼 수 있는가」에 일절 관여하지 않는다.
 *
 * <p>판정 재료는 <b>현장 소속을 가졌는가</b>다({@code siteScopeAll === false} 이면서 현장 이름이
 * 하나라도 있는 사람). 「부서가 없으니 현장이겠지」로 추정하지 않는다 — 부서를 겸한 현장 인원이
 * 본사로 읽히던 것이 지금까지 문제의 뿌리였다(백엔드 {@code SITE_ASSIGNED} 축과 같은 판단).
 *
 * <p>관리자는 기본 순서 그대로다. 전 현장을 걸쳐 보는 자리라 어느 쪽이 먼저랄 것이 없고,
 * 화면을 만들고 시험하는 사람이 곧 관리자여서 기본 배치를 그대로 보는 편이 낫다.
 */
export function resolveSiteFirst(session: Session | undefined): boolean {
  if (!session?.authenticated) return false;
  if (isAdministrator(session.user?.userType)) return false;
  // 옛 응답에는 이 칸이 없다. 없으면 기본 순서다 — 순서를 바꾸는 쪽이 예외이므로 그쪽에 근거를 요구한다.
  if (session.siteScopeAll !== false) return false;
  return (session.siteScopeNames ?? []).length > 0;
}

/**
 * <b>내가 맡은 현장 id 들</b> — 현장 정보가 그 현장을 앞으로 올리고 배지를 다는 데 쓴다
 * (사용자 지시 2026-09-04).
 *
 * <p>{@link resolveSiteFirst} 와 같은 재료를 본다. 저쪽은 "현장 사람인가" 하나만 묻고 여기서는
 * <b>어느 현장인가</b>까지 쓴다 — 판정 재료가 하나여야 두 화면이 같은 사람을 같게 본다.
 *
 * <h2>이름이 아니라 id 다</h2>
 * 소속의 원천은 이름(text)이고 서버의 문도 이름으로 견주지만({@code SiteScopeGuard} · v1 스키마),
 * <b>화면이 짚는 열쇠는 id 다</b>. 이름으로 맞추면 「염창동 현장」·「염창동현장」 한 칸 차이로 조용히
 * 빗나가는데, 그 실패가 화면에는 「내 현장이 앞에 안 오네」 로만 보여 원인을 짚을 수 없다.
 * 서버가 마스터에 맞춰 낸 id 를 그대로 받는다({@code SessionResponse.siteScopeIds}).
 *
 * <p>관리자에게는 빈 목록이다. 전 현장을 걸쳐 보는 자리라 앞으로 올릴 「내 현장」 이 없다.
 */
export function resolveMySiteIds(session: Session | undefined): string[] {
  if (!session?.authenticated) return [];
  if (isAdministrator(session.user?.userType)) return [];
  // 옛 응답에는 이 칸이 없다. 제한이 없는 사람은 맡은 현장도 따로 없다.
  if (session.siteScopeAll !== false) return [];
  return session.siteScopeIds ?? [];
}

/**
 * 내 현장을 앞으로 끌어올린다. <b>고른 정렬은 그대로 지킨다</b>.
 *
 * <p>{@code Array.sort} 가 안정 정렬이라 같은 편(내 현장끼리·남의 현장끼리) 안에서는 들어온
 * 순서가 유지된다 — 사용자가 고른 기준(공정율·착공일…)이 두 덩이 안에서 그대로 살아 있다.
 * 「정렬 없음」 으로 두었을 때도 현장 관리에서 넘어온 순서가 각 덩이 안에서 지켜진다.
 */
export function mineFirst<T>(items: T[], idOf: (item: T) => string, mine: ReadonlySet<string>): T[] {
  if (mine.size === 0) return items;

  const isMine = (item: T) => (mine.has(idOf(item)) ? 1 : 0);
  return [...items].sort((a, b) => isMine(b) - isMine(a));
}

/**
 * 사이드바 하위 메뉴의 현장 우선 순서. 그룹 키 → 그 안의 메뉴 키 순서.
 *
 * <p>전부 적는다(부분 목록이 아니다) — 빠진 키는 뒤로 밀리므로, 나중에 탭이 하나 늘었을 때
 * 조용히 맨 뒤로 가는 것보다 여기 적히지 않은 것이 눈에 띄는 편이 낫다.
 */
export const SITE_FIRST_MENU_ORDER: Record<string, string[]> = {
  // 대시보드는 본사·현장을 함께 요약한다 — 「전체」와 같이 첫 자리를 지킨다.
  work_weekly: ['dashboard', 'meetings_site', 'meetings_completed', 'meetings_hq'],
  // 「전체」는 달력이라 현장·본사 어느 쪽도 아니다 — 아래 탭 순서와 같이 자리를 지킨다.
  work_monthly: ['calendar', 'monthly_field', 'monthly_site', 'monthly_hq'],
};

/** 상단 탭의 현장 우선 순서. 탭 키는 메뉴 키와 다르다(`WeeklyWorkPage`·`MonthlyWorkPage`). */
export const SITE_FIRST_WEEKLY_TABS = ['dashboard', 'site', 'completed', 'hq'];
/** 「전체」는 달력이라 현장·본사 어느 쪽도 아니다 — 자리를 지킨다. */
export const SITE_FIRST_MONTHLY_TABS = ['all', 'field', 'site', 'hq'];

/**
 * 주어진 키 순서대로 다시 세운다. 목록에 없는 키는 <b>뒤에 원래 순서로</b> 남는다.
 *
 * <p>{@code Array.sort} 는 안정 정렬이라 0 을 돌려주면 원래 순서가 지켜진다.
 */
export function orderByKeys<T>(items: T[], keyOf: (item: T) => string, order: string[]): T[] {
  if (order.length === 0) return items;

  const rank = new Map(order.map((key, index) => [key, index] as const));
  return [...items].sort((a, b) => {
    const left = rank.get(keyOf(a));
    const right = rank.get(keyOf(b));
    if (left === undefined && right === undefined) return 0;
    if (left === undefined) return 1;
    if (right === undefined) return -1;
    return left - right;
  });
}
